/**
 * Safely extract error message from unknown error type
 * 
 * @param error - The caught error (unknown type)
 * @param fallback - Default message if error cannot be parsed
 * @returns A safe string error message
 * 
 * @example
 * try {
 *   await apiCall();
 * } catch (error: unknown) {
 *   toast.error(getErrorMessage(error));
 * }
 */
export function getErrorMessage(error: unknown, fallback = "An error occurred"): string {
  if (error instanceof Error) {
    return error.message;
  }
  
  if (typeof error === "string") {
    return error;
  }
  
  if (error && typeof error === "object" && "message" in error) {
    return String(error.message);
  }
  
  return fallback;
}

/**
 * Type guard to check if error is an Error instance
 */
export function isError(error: unknown): error is Error {
  return error instanceof Error;
}

/**
 * Type guard to check if error has a message property
 */
export function hasMessage(error: unknown): error is { message: string } {
  return (
    error !== null &&
    typeof error === "object" &&
    "message" in error &&
    typeof (error as any).message === "string"
  );
}
